<script lang="ts" setup>
import BaseCardFooter from "@/components/BaseCardFooter.vue";
import BaseCardHeader from "@/components/BaseCardHeader.vue";
import { Container, Footer, Header } from "@/types/base-card";
import { onMounted, ref } from "vue";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  closable?: boolean;
  collapsible?: boolean;
  collapsed?: boolean;
  container?: Container;
  header?: Header;
  footer?: Footer;
  hasPageHeading?: boolean;
}>();

const card = ref<HTMLElement | null>(null);

const scrollToTop = () => {
  card.value?.classList.add("scroll-mt-20");

  card.value?.scrollIntoView({
    behavior: "smooth",
  });

  card.value?.classList.remove("scroll-mt-20");
};

defineExpose({ scrollToTop });

const emit = defineEmits(["close"]);

function close(): void {
  emit("close");
}

onMounted(() => {
  _collapsed.value = props.collapsed;
});

const _collapsed = ref(false);

const hoveringFooter = ref<boolean>(false);
</script>

<template>
  <article
    ref="card"
    :class="[
      container?.class,
      hoveringFooter ? 'border-green-600/75' : 'border-green-900/75',
    ]"
    class="card flex w-full flex-col divide-y divide-green-900/75 border-b-2 border-t-2 bg-black/50 text-white shadow-md sm:border-2"
  >
    <BaseCardHeader
      v-if="$slots.heading"
      v-model:collapsed="_collapsed"
      :class="header?.class"
      :closable="closable"
      :collapsible="collapsible"
      :has-page-heading="hasPageHeading"
      :heading="header?.heading"
      @close="close"
    >
      <template v-if="$slots.subheading" #subheading>
        <slot name="subheading" />
      </template>

      <template v-if="$slots.headerIcon" #icon>
        <slot name="headerIcon" />
      </template>

      <template v-if="$slots.headerActions" #actions>
        <slot name="headerActions" />
      </template>

      <slot name="heading" />
    </BaseCardHeader>

    <div
      v-if="!_collapsed"
      class="flex grow flex-col gap-4 p-4 shadow-inner"
      v-bind="$attrs"
    >
      <slot />
    </div>

    <BaseCardFooter
      v-if="$slots.footer || footer?.content"
      :class="footer?.class"
      :hover-light-up="footer?.hoverLightUp"
      :href="footer?.href"
      :on-click="footer?.onClick"
      @hovering="hoveringFooter = $event"
    >
      <slot v-if="$slots.footer" name="footer" />
      <template v-else>{{ footer.content }}</template>
    </BaseCardFooter>
  </article>
</template>

<style scoped>
.card :deep(a) {
  @apply w-fit border-b-2 border-green-300 pb-0 font-medium transition-all;
  border-image: linear-gradient(to right, #6ee7b7, #fcd34d) 1;
}
</style>
